import { TASKS, OPERATIONS } from "constants/actions";

const constructDates = (values) => {
  let dateRange = values.dateRange;
  let datetimePerformed = values.datetimePerformed ?? {};
  let finalDateRange, finalDatetimePerformed, finalDatetimePerformedEnd;

  const isDateRangePopulated = Boolean(dateRange.from || dateRange.to);
  const isDatetimePerformedPopulated = Boolean(
    datetimePerformed.from && datetimePerformed.to,
  );

  if (isDateRangePopulated) {
    finalDateRange = {
      lower: dateRange.from,
      upper: dateRange.to,
    };
  } else {
    if (isDatetimePerformedPopulated) {
      finalDateRange = null;
    }
  }

  if (isDatetimePerformedPopulated) {
    finalDatetimePerformed = datetimePerformed.from;
    finalDatetimePerformedEnd = datetimePerformed.to;
  }

  return {
    dateRange: finalDateRange,
    datetimePerformed: finalDatetimePerformed,
    datetimePerformedEnd: finalDatetimePerformedEnd,
  };
};

const constructTimeTaken = (values) => {
  let timeTaken = values.timeTaken || 0;
  if (values?.equipment?.length > 0) {
    timeTaken = values.equipment.reduce((acc, curr) => acc + curr.duration, 0);
  }
  return timeTaken;
};

const constructAttachments = (values) => {
  const attachments = values.attachments ?? [];
  return attachments.map((file) => ({
    ...file,
    fileUrl: file.url,
  }));
};

const removeUnnecessaryFields = (values) => {
  const {
    shouldCheckEnemyId,
    stressType,
    status,
    initialStatus,
    ...finalValues
  } = values;

  return finalValues;
};

const turnAnalysisQuantitiesToNumbers = (analysis) => {
  if (typeof analysis !== "object") return analysis;

  const finalAnalysis = Object.entries(analysis).reduce((acc, [key, value]) => {
    const quantity = Number(value.quantity);
    return {
      ...acc,
      [key]: {
        ...value,
        quantity,
      },
    };
  }, {});
  return finalAnalysis;
};

const constructWaterPayload = (values) => {
  const dates = constructDates(values);
  const timeTaken = constructTimeTaken(values);

  return {
    ...values,
    ...dates,
    timeTaken,
  };
};

const constructFertilizePayload = (values) => {
  const dates = constructDates(values);
  const timeTaken = constructTimeTaken(values);

  const { agroMetaValue, ...finalFertilizeAction } =
    values.fertilizeAction || {};

  return {
    ...values,
    fertilizeAction: finalFertilizeAction,
    ...dates,
    timeTaken,
  };
};
const constructDrugPayload = (values) => {
  const dates = constructDates(values);
  const timeTaken = constructTimeTaken(values);

  const { drugAction } = values;
  const { chemicalId, agroMetaValue, ...finalDrugAction } = drugAction;

  return {
    ...values,
    drugAction: finalDrugAction,
    ...dates,
    timeTaken,
  };
};

const constructDiagnosePayload = (values, helpers = {}) => {
  const dates = constructDates(values);
  const attachments = constructAttachments(values);

  const { diagnoseOperation } = helpers;
  const { quantity, quantityUnits, analysis, ...diagnoseAction } =
    values?.diagnoseAction || {};

  // Handle analysis operation
  if (
    diagnoseOperation === OPERATIONS.DIAGNOSE.SOIL_SAMPLING ||
    diagnoseOperation === OPERATIONS.DIAGNOSE.LEAF_ANALYSIS
  ) {
    return {
      ...values,
      ...dates,
      attachments,
      diagnoseAction: {
        ...diagnoseAction,
        analysis: turnAnalysisQuantitiesToNumbers(analysis),
      },
    };
  }

  // handle insect operation
  if (diagnoseOperation === OPERATIONS.DIAGNOSE.INSECT_MONITORING) {
    return {
      ...values,
      ...dates,
      attachments,
      diagnoseAction: {
        ...diagnoseAction,
        quantity,
        quantityUnits,
      },
    };
  }

  return {
    ...values,
    attachments,
    diagnoseAction,
    ...dates,
  };
};
const constructOtherPayload = (values, helpers = {}) => {
  const dates = constructDates(values);
  const timeTaken = constructTimeTaken(values);

  const { otherOperation } = helpers;

  const {
    quantity,
    quantityUnits,
    ploughingType,
    mulchType,
    ...restOtherAction
  } = values.otherAction;

  if (otherOperation === OPERATIONS.OTHER.HARVEST) {
    return {
      ...values,
      otherAction: {
        ...restOtherAction,
        quantity,
        quantityUnits,
      },
      ...dates,
      timeTaken,
    };
  }
  if (otherOperation === OPERATIONS.OTHER.PLOWING) {
    return {
      ...values,
      otherAction: {
        ...restOtherAction,
        ploughingType,
      },
      ...dates,
      timeTaken,
    };
  }
  if (otherOperation === OPERATIONS.OTHER.MULCH) {
    return {
      ...values,
      otherAction: {
        ...restOtherAction,
        mulchType,
      },
      ...dates,
      timeTaken,
    };
  }
  return {
    ...values,
    ...dates,
    timeTaken,
  };
};

const constructObservationPayload = (values) => {
  const dates = constructDates(values);
  const attachments = constructAttachments(values);

  return {
    ...values,
    ...dates,
    attachments,
  };
};

export const constructActionPayload = (type, values, helpers) => {
  const reducedValues = removeUnnecessaryFields(values);
  switch (type) {
    case TASKS.watering:
      return constructWaterPayload(reducedValues, helpers);
    case TASKS.fertilize:
      return constructFertilizePayload(reducedValues, helpers);
    case TASKS.drug:
      return constructDrugPayload(reducedValues, helpers);
    case TASKS.diagnose:
      return constructDiagnosePayload(reducedValues, helpers);
    case TASKS.other:
      return constructOtherPayload(reducedValues, helpers);
    case TASKS.observation:
      return constructObservationPayload(reducedValues, helpers);
    default:
      return;
  }
};
